// @ts-check
import { saveValueToStorage, getValueFromStorage } from '../utils/index.js';

/** @typedef {import('./get-settings').CommerceConnectorSettings} CommerceConnectorSettings */
/**
 * @typedef {Object} ZIPCodeParams
 * @property {string} countryLanguageCode
 * @property {RegExp} pattern Validation pattern
 * @property {number} length Maximum length
 * @property {boolean} detect Whether value should be geolocated
 */

const AUTO_DETECT_ZIP_CODE_SESSION_STORAGE_KEY = 'STATDEMANDBASE';
const AUTO_DETECT_ZIP_CODE_PROPERTY_NAME = 'registry_zip_code';
const USER_ZIP_CODE_LOCAL_STORAGE_KEY = 'userZipCode';

/**
 * Validate ZIP code field
 * @param {string} value
 * @param {ZIPCodeParams} params
 * @returns {boolean}
 */
export function isZIPCodeValid(value, { pattern }) {
  return pattern.test(value);
}

/**
 * Save user zip into local storage. Zip code will be saved according country-lang code.
 * @param {string} value
 * @param {ZIPCodeParams} params
 */
export function saveZIPCodeValue(value, { countryLanguageCode }) {
  /** @type {Object} */
  const localeZipCodeMap = getValueFromStorage(USER_ZIP_CODE_LOCAL_STORAGE_KEY, { defaultValue: {} });

  localeZipCodeMap[countryLanguageCode] = value;
  saveValueToStorage(USER_ZIP_CODE_LOCAL_STORAGE_KEY, localeZipCodeMap);
}

/**
 * @param {ZIPCodeParams} params
 * @returns {string | null}
 */
export function restoreZIPCodeValue({ countryLanguageCode, length }) {
  /** @type {string | undefined} */
  const localeZIPCode = getValueFromStorage(USER_ZIP_CODE_LOCAL_STORAGE_KEY, {
    namespace: countryLanguageCode
  });

  if (localeZIPCode) {
    // The reason why saved ZIP code is sliced to max length (instead of rejecting as invalid) is unknown
    return localeZIPCode.slice(0, length);
  }

  return null;
}

/**
 * @returns {string | null}
 */
export function detectZIPCodeValue() {
  const geolocatedZIPCode = getValueFromStorage(AUTO_DETECT_ZIP_CODE_SESSION_STORAGE_KEY, {
    namespace: AUTO_DETECT_ZIP_CODE_PROPERTY_NAME,
    storage: window.sessionStorage
  });

  // TODO: Why do we consider `0` a valid ZIP code?
  if (geolocatedZIPCode || geolocatedZIPCode === 0) {
    return geolocatedZIPCode.toString();
  }

  return null;
}

/**
 * Return initial value of ZIP code
 * @param {ZIPCodeParams} params
 * @returns {string}
 */
export function getZIPCodeInitialValue(params) {
  const { detect, pattern } = params;

  const emptyValue = '';

  let value = emptyValue;

  // First take value from localStorage
  let savedValue = restoreZIPCodeValue(params);

  if (!savedValue && detect) {
    // Then take geolocated value from sessionStorage
    savedValue = detectZIPCodeValue();
  }

  // Only use value if it matches validation pattern
  if (pattern.test(savedValue)) {
    value = savedValue;
  }

  return value || emptyValue;
}
